import {
  IntegrationType,
  PaywallApi,
  PaywallProps,
  PaywallSession,
} from '@accedo/one-plugin-sdk';
import { ErrorBoundary } from '@dynamic/ui';
import { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';

import { Errors } from '#/errors/errors';
import { OneError } from '#/errors/oneError';
import { useConfig } from '#/hooks/useConfig';
import { usePaymentMethod } from '#/hooks/usePaymentMethod';
import { usePaywallConfig } from '#/hooks/usePaywallConfig';
import { useWaitForImperativeRef } from '#/hooks/useWaitForImperativeRef';
import { getPaywallSession } from '#/plugins/paywall/utils';
import { authActions, useAuthStore } from '#/store/auth/auth.store';
import { useBootStore } from '#/store/boot/boot.store';
import { paywallActions, usePaywallStore } from '#/store/paywall/paywall.store';
import { useTranslationStore } from '#/store/translation/translation.store';

import { pluginManager } from '../pluginManager';

export const PaywallManager = () => {
  const { isPluginManagerReady } = useBootStore();

  const apiRef = useRef<PaywallApi>(null);
  const { isPluginReady } = usePaywallStore();
  const paywallConfig = usePaywallConfig();
  const { paymentMethod } = usePaymentMethod();
  const auth = useAuthStore();
  const { locale } = useTranslationStore();
  const config = useConfig();
  const isInAppEnabled = config.features.purchase?.inApp?.enabled || false;

  const { sessionEndpoint } = paywallConfig?.properties ?? {};

  // apiRef.current will change value on login/logout, so we need to re-init the paywallApi (ie: ignoring isPluginReady)
  useEffect(() => {
    if (apiRef.current) {
      paywallActions.initPlugin(apiRef);
    }
  }, [auth.isInitialised, auth.isLoggedIn, isInAppEnabled]);

  // workaround for a race condition in the useEffect above:
  // sometimes apiRef.current is the last thing getting a value (especially with throttled network).
  // but apiRef.current can't be a dependency because ref changes don't re-render.
  // this means the effect ran on every dependency change already, and will not run again.
  // so initPlugin won't be called, and components waiting for isPluginReady will wait forever.
  useWaitForImperativeRef(apiRef, () => {
    if (!isPluginReady) {
      paywallActions.initPlugin(apiRef);
    }
  });

  // refresh the expiry status on login
  useEffect(() => {
    if (auth.isLoggedIn && isPluginReady && paymentMethod) {
      paywallActions.refreshExpiryStatus(paymentMethod);
    }
  }, [auth.isLoggedIn, isPluginReady, paymentMethod]);

  /**
   * This function is no longer in use, and only maintained for backwards compatibility.
   * @see https://accedobroadband.jira.com/browse/ONEWEB-2319
   * Gets session token, but to make sure we are up to date we refresh the profile first.
   * And we analyze profile response to see if there is an entitlement error. If so,
   * we should prevent showing the paywall.
   */
  const getSession = useCallback<
    () => Promise<PaywallSession | null>
  >(async () => {
    const profile = await authActions.refreshProfile();

    if (profile?.errors?.entitlements) {
      throw new OneError(Errors.ENTITLEMENT_PROFILE_ERROR);
    }

    return sessionEndpoint
      ? getPaywallSession(sessionEndpoint).then(({ data }) => data)
      : Promise.resolve(null);
  }, [sessionEndpoint]);

  const PaywallComponent = useMemo(() => {
    if (!paywallConfig?.key || !isPluginManagerReady) {
      return null;
    }

    return pluginManager.getPluginComponent<PaywallProps>({
      type: IntegrationType.PAYWALL,
      id: paywallConfig.key,
      optional: true,
    });
  }, [paywallConfig, isPluginManagerReady]);

  if (!PaywallComponent) {
    return null;
  }

  return (
    <ErrorBoundary onError={PaywallComponent.onError}>
      <Suspense fallback={null}>
        <PaywallComponent
          apiRef={apiRef}
          authState={auth}
          getSession={getSession}
          key={`${auth.isLoggedIn}`}
          locale={locale}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

PaywallManager.displayName = 'PaywallManager';
